<template>
  <div>
    <template v-if="consignment.status === 'SUCCESS' || consignment.status === 'ERROR'">
      <b-button
        variant="primary"
        class="mb-1"
        type="submit"
        :disabled="requestIsLoading"
        @click="saveConsignmentRequest()"
      >
        {{ requestIsLoading ? t('Loading') : t('Save Changes') }}
      </b-button>
      <b-row>
        <consignor-information-form
          :consignorInformations="consignorInformations"
          :editable="true"
        />
        <consignee-information-form
          :consigneeInformations="consigneeInformations"
          :editable="true"
        />
      </b-row>
      <b-row>
        <b-col>
          <b-card no-body>
            <b-card-body>
              <h4 class="mb-1 font-weight-bold">
                {{ t('Package Informations') }}
              </h4>
              <ConsignmentPackage
                v-for="(item, index) in consignment.data.packages"
                :key="index"
                :title="`${t('Package')} ${index + 1}`"
                :data="item"
                :class="[index !== 0 && 'mt-1']"
              />
            </b-card-body>
          </b-card>
        </b-col>
        <b-col>
          <ConsignmentDocuments
            :documents="consignment.data.documents"
            :consignment-id="consignment.data.id"
            :permissions="consignment.data.permissions"
            @reloadData="refreshDocuments"
          />
        </b-col>
      </b-row>
    </template>
    <PageLoading v-else-if="consignment.status === 'LOADING'" />
  </div>
</template>
<script>
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import PageLoading from '@core/components/PageLoading.vue';
import { BRow, BCol, BCard, BCardBody, BButton } from 'bootstrap-vue';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';
import ConsignmentPackage from '@/components/ConsignmentPackage.vue';
import ConsignmentDocuments from '@/views/pages/consignments/ConsignmentDocuments.vue';
import ConsignorInformationForm from '@/components/ConsignorInformationForm.vue';
import ConsigneeInformationForm from '@/components/ConsigneeInformationForm.vue';

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,

    ConsignmentPackage,
    ConsignmentDocuments,
    ConsignorInformationForm,
    ConsigneeInformationForm,

    PageLoading
  },
  mixins: [helper],
  data() {
    return {
      consignment: {
        data: [],
        status: 'PENDING',
        message: ''
      },
      consignorInformations: {
        country: '',
        countryIso3: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: ''
      },
      consigneeInformations: {
        country: '',
        countryIso3: '',
        state: '',
        city: '',
        postal_code: '',
        name: '',
        attention_name: '',
        attention_email: '',
        phone_number: '',
        address: ''
      },
      minimumPostalCodeLength: 3,
      requestIsLoading: false
    };
  },
  async mounted() {
    this.initData();
  },
  methods: {
    refreshDocuments() {
      const consignmentId = this.$route.params.id;

      useService
        .getConsignmentDetails(consignmentId)
        .then(response => {
          this.consignment = {
            data: {
              ...this.consigment.data,
              packages: response.data.packages,
              documents: response.data.documents
            },
            status: 'SUCCESS',
            message: ''
          };
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            this.consignment = {
              data: null,
              status: 'ERROR',
              message: messages.map(item => item.message).join(', ')
            };
          }
        });
    },
    initData() {
      const consignmentId = this.$route.params.id;

      this.consignment = {
        data: [],
        status: 'LOADING',
        message: ''
      };

      useService
        .getConsignmentDetails(consignmentId)
        .then(response => {
          this.consignment = {
            data: response.data,
            status: 'SUCCESS',
            message: ''
          };
          this.consignorInformations = response.data.consignor;
          this.consigneeInformations = response.data.consignee;
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            this.consignment = {
              data: null,
              status: 'ERROR',
              message: messages.map(item => item.message).join(', ')
            };
          }
        });
    },
    saveConsignmentRequest() {
      const consignmentId = this.$route.params.id;

      const requestData = {
        ...this.consignment.data,
        consignor: {
          ...this.consignorInformations,
          country: this.consignorInformations.country.iso3,
          city: this.consignorInformations.city?.text || this.consignorInformations.city,
          state: this.consignorInformations.state?.text || this.consignorInformations.state,
          postal_code:
            this.consignorInformations.postal_code?.value || this.consignorInformations.postal_code
        },
        consignee: {
          ...this.consigneeInformations,
          country: this.consigneeInformations.country.iso3,
          city: this.consigneeInformations.city?.text || this.consigneeInformations.city,
          state: this.consigneeInformations.state?.text || this.consigneeInformations.state,
          postal_code:
            this.consigneeInformations.postal_code?.value || this.consigneeInformations.postal_code
        }
      };

      this.requestIsLoading = true;

      useService
        .saveConsignment(consignmentId, requestData)
        .then(response => {
          if (response.status) {
            this.showSuccessMessage(response.message);
          }
        })
        .catch(errors => {
          if (typeof errors === 'object' && errors && errors.length) {
            errors.map(error => {
              return this.showErrorMessage(error.message);
            });
          }
        })
        .finally(() => {
          this.requestIsLoading = false;
        });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
